import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Empty, Row, Typography } from 'antd'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { getCreditMetrics, Granularity, MetricsPeriod } from '../../../Actions/operationsMetricsApi'
import StatCard from './StatCard'
import { formatCompact, formatNumber } from './formatters'

interface Props {
  period: MetricsPeriod
  granularity: Granularity
}

/** 🪙 크레딧 — 충전 vs 소비 추이 라인차트 + 합계 (확정 receipt 기준) */
export default function CreditsSection({ period, granularity }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'credits', period, granularity],
    queryFn: () => getCreditMetrics({ ...period, granularity }),
  })

  if (error) return <Alert type="error" message="크레딧 지표를 불러오지 못했습니다." description={(error as Error).message} />

  const trend = data?.trend ?? []

  return (
    <Card title="🪙 크레딧 (확정 거래 기준)" size="small">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={18}>
          <Typography.Text strong>충전 vs 소비 추이</Typography.Text>
          {trend.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="기간 내 거래 없음" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" fontSize={12} />
                <YAxis tickFormatter={(v) => formatCompact(Number(v))} fontSize={12} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Legend />
                <Line type="monotone" dataKey="charged" name="충전" stroke="#1677ff" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="consumed" name="소비" stroke="#fa8c16" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Col>
        <Col xs={24} md={6}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <StatCard
                title="총 충전 크레딧"
                value={data ? formatNumber(data.totalCharged) : '—'}
                valueColor="#1677ff"
                loading={isLoading}
              />
            </Col>
            <Col span={24}>
              <StatCard
                title="총 소비 크레딧"
                value={data ? formatNumber(data.totalConsumed) : '—'}
                valueColor="#fa8c16"
                loading={isLoading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  )
}